import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveCueStatus = 'idle' | 'saving' | 'saved' | 'error';

type UseAutosaveCueOptions = {
  /** How long to show "Saved" before returning to idle. Default 2000ms. */
  savedDisplayMs?: number;
};

/**
 * Status-board cue for silent autosave surfaces:
 * idle → saving → saved → idle (or saving → error, kept until the next save).
 * Pair with `AutosaveStatusCue`. Prefer this over per-page timer logic.
 */
export function useAutosaveCue(options?: UseAutosaveCueOptions) {
  const savedDisplayMs = options?.savedDisplayMs ?? 2000;
  const [status, setStatus] = useState<AutosaveCueStatus>('idle');
  const clearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasPendingRef = useRef(false);
  /** True only after markSaving — blocks spurious markSaved on hydrate. */
  const saveInFlightRef = useRef(false);

  const clearSavedTimer = useCallback(() => {
    if (clearRef.current) {
      clearTimeout(clearRef.current);
      clearRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSavedTimer(), [clearSavedTimer]);

  const scheduleIdle = useCallback(() => {
    clearSavedTimer();
    clearRef.current = setTimeout(() => {
      clearRef.current = null;
      setStatus('idle');
    }, savedDisplayMs);
  }, [clearSavedTimer, savedDisplayMs]);

  const markSaving = useCallback(() => {
    clearSavedTimer();
    saveInFlightRef.current = true;
    setStatus('saving');
  }, [clearSavedTimer]);

  const markSaved = useCallback(() => {
    if (!saveInFlightRef.current) {
      return;
    }
    saveInFlightRef.current = false;
    setStatus('saved');
    scheduleIdle();
  }, [scheduleIdle]);

  // Errors stay visible until the next save attempt: a cue that falls back to
  // "saves automatically" after 2s hides that nothing was saved (DEV-551).
  const markError = useCallback(() => {
    clearSavedTimer();
    saveInFlightRef.current = false;
    setStatus('error');
  }, [clearSavedTimer]);

  const markIdle = useCallback(() => {
    clearSavedTimer();
    saveInFlightRef.current = false;
    setStatus('idle');
  }, [clearSavedTimer]);

  /**
   * Sync from a boolean pending flag (e.g. mutation.isPending).
   * Flashes Saved when pending transitions true → false without error.
   */
  const syncFromPending = useCallback(
    (pending: boolean, opts?: { errored?: boolean }) => {
      if (pending) {
        wasPendingRef.current = true;
        markSaving();
        return;
      }
      if (!wasPendingRef.current) {
        return;
      }
      wasPendingRef.current = false;
      if (opts?.errored) {
        markError();
      } else {
        markSaved();
      }
    },
    [markError, markSaved, markSaving],
  );

  return {
    status,
    markSaving,
    markSaved,
    markError,
    markIdle,
    syncFromPending,
  };
}
